import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { useAuth } from '../../shared/auth/AuthContext';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import { Badge, Button, Input, LoadingSkeleton, Modal, Pagination, Select } from '@nexora/design-system';
import { useToast } from '@nexora/design-system';
import './obligations.css';

/* ───────────────────────────────────────────────────────────────────────────
   Central de Guias — /obligations/guias.
   Consome: GET/POST /obligations/guias (lista paginada + cadastro),
   PATCH /obligations/guias/:id/pay (marcar paga) e POST /guias/:id/cancel.
   GuiaRecolhimentoDto: tipoGuia, tipoTributo, competencia ('MM/AAAA', texto),
   vencimento (ISO → formatDate), valor, statusAutomacao, status.
   Pagar/Cancelar desabilitados para PAGO/CANCELADO; o backend valida as
   transições inválidas.
   ─────────────────────────────────────────────────────────────────────────── */

type GuiaStatus = 'PENDENTE' | 'AGENDADO' | 'PAGO' | 'VENCIDO' | 'CANCELADO';

interface GuiaRecolhimento {
  id: string;
  guiaNumero: string;
  tipoGuia: string;
  tipoTributo: string;
  competencia: string;
  vencimento: string;
  valor: number;
  statusAutomacao: string;
  status: GuiaStatus;
  pagoEm: string | null;
}

interface PaginatedGuias {
  data: GuiaRecolhimento[];
  total: number;
  totalPages: number;
}

const STATUS_LABEL: Record<string, string> = {
  PENDENTE: 'Pendente',
  AGENDADO: 'Agendado',
  PAGO: 'Pago',
  VENCIDO: 'Vencido',
  CANCELADO: 'Cancelado',
};

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'error' | 'neutral' | 'info'> = {
  PENDENTE: 'warning',
  AGENDADO: 'info',
  PAGO: 'success',
  VENCIDO: 'error',
  CANCELADO: 'neutral',
};

const AUTOMACAO_LABEL: Record<string, string> = {
  SEM_AUTOMACAO: 'Sem automação',
  AGENDADO: 'Agendado',
  PAGO: 'Pago',
  FALHA: 'Falha',
};

function NovaGuiaModal({
  isOpen,
  onClose,
  onCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const { addToast } = useToast();
  const [form, setForm] = useState({ tipoGuia: 'DARF', tipoTributo: 'ICMS', competencia: '', vencimento: '', valor: '' });

  const createMutation = useMutation({
    mutationFn: () =>
      apiClient.post<GuiaRecolhimento>('/obligations/guias', {
        tipoGuia: form.tipoGuia,
        tipoTributo: form.tipoTributo,
        competencia: form.competencia,
        vencimento: new Date(form.vencimento).toISOString(),
        valor: Number(form.valor),
        statusAutomacao: 'SEM_AUTOMACAO',
      }),
    onSuccess: () => {
      addToast('Guia cadastrada', 'success');
      onCreated();
      onClose();
      setForm({ tipoGuia: 'DARF', tipoTributo: 'ICMS', competencia: '', vencimento: '', valor: '' });
    },
    onError: (e) => addToast(e instanceof Error ? e.message : 'Erro ao cadastrar guia', 'error'),
  });

  const invalido = !form.competencia || !form.vencimento || !form.valor || !Number.isFinite(Number(form.valor));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Nova guia de recolhimento"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" disabled={invalido || createMutation.isPending} onClick={() => createMutation.mutate()}>
            Cadastrar
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <Select label="Tipo da guia" value={form.tipoGuia} onChange={(e) => setForm((f) => ({ ...f, tipoGuia: e.target.value }))}>
          <option value="DARF">DARF</option>
          <option value="GNRE">GNRE</option>
          <option value="DAS">DAS</option>
        </Select>
        <Select label="Tipo / Tributo" value={form.tipoTributo} onChange={(e) => setForm((f) => ({ ...f, tipoTributo: e.target.value }))}>
          <option value="ICMS">ICMS</option>
          <option value="IPI">IPI</option>
          <option value="PIS">PIS</option>
          <option value="COFINS">COFINS</option>
        </Select>
        <Input label="Competência (MM/AAAA)" value={form.competencia} onChange={(e) => setForm((f) => ({ ...f, competencia: e.target.value }))} placeholder="09/2023" />
        <Input label="Vencimento" type="date" value={form.vencimento} onChange={(e) => setForm((f) => ({ ...f, vencimento: e.target.value }))} />
        <Input label="Valor (R$)" type="number" min={0} step="0.01" value={form.valor} onChange={(e) => setForm((f) => ({ ...f, valor: e.target.value }))} placeholder="0,00" />
      </div>
    </Modal>
  );
}

export function GuiasPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const [statusFilter, setStatusFilter] = useState('PENDENTE');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const pageSize = 20;

  const listQuery = useQuery({
    queryKey: ['obligations', tenantId, 'guias', statusFilter, page],
    queryFn: () =>
      apiClient.get<PaginatedGuias>('/obligations/guias', {
        params: {
          page: String(page),
          limit: String(pageSize),
          ...(statusFilter !== 'ALL' ? { status: statusFilter } : {}),
        },
      }),
    enabled: !!tenantId,
  });

  const invalidateGuias = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['obligations', tenantId, 'guias'] });
  };

  const payMutation = useMutation({
    mutationFn: (id: string) => apiClient.patch<GuiaRecolhimento>(`/obligations/guias/${id}/pay`, {}),
    onSuccess: () => {
      addToast('Guia marcada como paga', 'success');
      invalidateGuias();
    },
    onError: (e) => addToast(e instanceof Error ? e.message : 'Erro ao pagar guia', 'error'),
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => apiClient.post<GuiaRecolhimento>(`/guias/${id}/cancel`),
    onSuccess: () => {
      addToast('Guia cancelada', 'success');
      invalidateGuias();
    },
    onError: (e) => addToast(e instanceof Error ? e.message : 'Erro ao cancelar guia', 'error'),
  });

  const { data, isLoading, error } = listQuery;
  const guias = data?.data ?? [];
  const totalPages = Math.max(1, data?.totalPages ?? 1);

  return (
    <div className="obl-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Central de Guias</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Guias de recolhimento pendentes, vencimento e status de automação.
          </p>
        </div>
        <Button variant="primary" onClick={() => setShowCreate(true)}>
          Nova guia
        </Button>
      </div>

      <div className="obl-toolbar">
        <Select
          label="Status"
          selectSize="sm"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="ALL">Todos</option>
          {Object.entries(STATUS_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <span className="obl-count">{data?.total ?? 0} guias</span>
      </div>

      <div className="obl-card">
        <div className="obl-card__head">
          <h2 className="obl-card__title">Guias</h2>
        </div>

        {isLoading && (
          <div style={{ padding: '1rem' }}>
            <LoadingSkeleton count={6} height="40px" />
          </div>
        )}

        {error && !isLoading && (
          <div className="obl-state">
            <p className="obl-state__title">Erro ao carregar guias</p>
            <p className="obl-state__detail">{(error as Error).message}</p>
            <Button variant="outline" size="sm" onClick={() => void listQuery.refetch()}>
              Tentar novamente
            </Button>
          </div>
        )}

        {!isLoading && !error && guias.length === 0 && (
          <div className="obl-state">
            <span className="material-symbols-outlined obl-state__icon" aria-hidden="true">
              receipt_long
            </span>
            <p className="obl-state__detail">Nenhuma guia encontrada.</p>
          </div>
        )}

        {!isLoading && !error && guias.length > 0 && (
          <>
            <div>
              {guias.map((guia) => (
                <div key={guia.id} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      {guia.tipoGuia} · {guia.tipoTributo}
                      <span className="obl-sub">{guia.guiaNumero}</span>
                    </div>
                  </div>
                  <div className="obl-nowrap" style={{ fontSize: '0.875rem' }}>
                    {guia.competencia}
                  </div>
                  <div className="obl-nowrap" style={{ fontSize: '0.875rem' }}>
                    {formatDate(guia.vencimento)}
                  </div>
                  <div className="obl-num" style={{ minWidth: 110 }}>
                    {formatCurrency(guia.valor)}
                  </div>
                  <div className="obl-nowrap" style={{ fontSize: '0.875rem' }}>
                    {AUTOMACAO_LABEL[guia.statusAutomacao] ?? guia.statusAutomacao}
                  </div>
                  <Badge variant={STATUS_VARIANT[guia.status] ?? 'neutral'} size="sm">
                    {STATUS_LABEL[guia.status] ?? guia.status}
                  </Badge>
                  <div className="obl-actions" style={{ display: 'flex', gap: '0.375rem' }}>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={guia.status === 'PAGO' || guia.status === 'CANCELADO' || payMutation.isPending}
                      onClick={() => payMutation.mutate(guia.id)}
                    >
                      Pagar
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={guia.status === 'PAGO' || guia.status === 'CANCELADO' || cancelMutation.isPending}
                      onClick={() => cancelMutation.mutate(guia.id)}
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="obl-footer">
              <span className="obl-count">
                Página {page} de {totalPages}
              </span>
              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            </div>
          </>
        )}
      </div>

      <NovaGuiaModal isOpen={showCreate} onClose={() => setShowCreate(false)} onCreated={() => void listQuery.refetch()} />
    </div>
  );
}